import './AssetMetadaa.scss';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../inputField/InputField';
import MultiSelectDropdown from '../multiSelectDropdown/MultiSelectDropdown';
import DealershipDropdown from '../dealershipDropdown/DealershipDropdown';
import { userStore } from '../../store';
import { AI_CLASSIFICATION, AI_DISCLOSURE, AI_HUMAN_REVIEWED } from '../../js/_constants';

const languagesValues = [
  { value: "nn", label: "Neutral" },
  { value: "de", label: "German" },
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "it", label: "Italian" },
  { value: "sq", label: "Albanian" },
  { value: "bs", label: "Bosnian" },
  { value: "bg", label: "Bulgarian" },
  { value: "ar", label: "Arabic" },
  { value: "bn", label: "Bengali" },
  { value: "hr", label: "Croatian" },
  { value: "cs", label: "Czech" },
  { value: "da", label: "Danish" },
  { value: "nl", label: "Dutch" },
  { value: "et", label: "Estonian" },
  { value: "fi", label: "Finnish" },
  { value: "el", label: "Greek" },
  { value: "he", label: "Hebrew" },
  { value: "hi", label: "Hindi" },
  { value: "hu", label: "Hungarian" },
  { value: "ga", label: "Irish" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "lv", label: "Latvian" },
  { value: "lt", label: "Lithuanian" },
  { value: "mk", label: "Macedonian" },
  { value: "ms", label: "Malay" },
  { value: "mt", label: "Maltese" },
  { value: "me", label: "Montenegrin" },
  { value: "no", label: "Norwegian" },
  { value: "fa", label: "Persian (Farsi)" },
  { value: "pl", label: "Polish" },
  { value: "pt", label: "Portuguese" },
  { value: "pa", label: "Punjabi" },
  { value: "ro", label: "Romanian" },
  { value: "ru", label: "Russian" },
  { value: "sr", label: "Serbian" },
  { value: "sk", label: "Slovak" },
  { value: "sl", label: "Slovenian" },
  { value: "es", label: "Spanish" },
  { value: "sv", label: "Swedish" },
  { value: "th", label: "Thai" },
  { value: "tr", label: "Turkish" },
  { value: "ur", label: "Urdu" },
  { value: "vi", label: "Vietnamese" },
  { value: "zh", label: "Chinese" }
];

const AssetMetadata= ({
    checkIfReady,
    setPayload,
    assetBlob,
    editMode,
    asset
}) => {
    const today = new Date();
    const { dealerships } = userStore();
    const { t } = useTranslation();
    const [title, setTitle] = useState(editMode ? asset.title : '');    
    const [kvps, setKvps] = useState(dealerships.list.length === 1 ? dealerships.list[0]['@id'].split('/')[4]: '')
    const [description, setDescription] = useState(editMode ? asset.description : '');
    const [expirationDate, setExpirationDate] = useState(editMode && asset.usageTo? asset.usageTo.split('T')[0] : '');
    const [language, setLanguage] = useState('')
    const [tags, setTags] = useState<string[]>(['system-tags:dealer_upload'])
    const [aiClassification, setAIClassification] = useState(editMode && asset?.aiCompliance ? asset.aiCompliance.classification : '');
    const [aiDisclosureRequired, setAIDisclosureRequired] = useState(editMode && asset?.aiCompliance ? asset.aiCompliance.disclosureRequired : '');
    const [aiHumanReviewed, setAIHumanReviewed] = useState(editMode && asset?.aiCompliance ? asset.aiCompliance.humanReviewed : '');

    useEffect(()=>{
        setPayload({
            title,
            language,
            tags,
            description,
            kvps,
            expirationDate,
            aiClassification,
            aiDisclosureRequired,
            aiHumanReviewed
        });

        !editMode && checkIfReady(
            kvps.length > 0 
            && title.length > 0
            && language.length > 0
            && description.length > 0
            && aiClassification.length > 0
            && typeof aiDisclosureRequired === 'boolean'
            && aiHumanReviewed.length > 0
        );
        editMode && checkIfReady(
            title.length > 0
            && description.length > 0
            && aiClassification.length > 0
            && typeof aiDisclosureRequired === 'boolean'
            && aiHumanReviewed.length > 0);
    },[title, language, description, kvps, expirationDate, aiClassification, aiDisclosureRequired, aiHumanReviewed]);

    // const onSelectTags = selectedOptions => {
    //     if (selectedOptions.length > 0) {
    //         setTags(selectedOptions.map(option => option.value));
    //     } else {
    //         setTags([]);
    //     }
    // };

    const onSelectLanguage = selectedOptions => {
        if (selectedOptions.length > 0) {
            setLanguage(selectedOptions.map(option => option.value)[0]);
        } else {
            setLanguage('');
        }
    };

    const onSelectAIClassification = selectedOptions => {
        if (selectedOptions.length > 0) {
            setAIClassification(selectedOptions.map(option => option.value)[0]);
        } else {
            setAIClassification(''); 
        }
    };
    
    const onSelectAIDisclosureRequired = selectedOptions => {
        if (selectedOptions.length > 0) {
            setAIDisclosureRequired(selectedOptions.map(option => option.value)[0]);
        } else {
            setAIDisclosureRequired('');
        }
    };
    
    const onSelectAIHumanReviewed = selectedOptions => {
        if (selectedOptions.length > 0) {
            setAIHumanReviewed(selectedOptions.map(option => option.value)[0]);
        } else {
            setAIHumanReviewed('');
        }
    };

    const handleDealerSelection = (selectedOptions) => {
        if (selectedOptions.length > 0) {
            setKvps(selectedOptions.map(option => option.value)[0]);
        } else {
            setKvps('');
        }
    };

    return (
        <div className='asset-metadata'>
                <InputField
                    label={t('title') + " *"}
                    name="title"
                    onValueChangeHandler={(value)=> setTitle(value)}
                    value={title}
                />
                <InputField
                    label={t('description') + " *"}
                    name="description"
                    onValueChangeHandler={(value) => setDescription(value)}
                    value={description}
                    inputType="textarea"
                />
                {!editMode && <>
                {/* <MultiSelectDropdown
                    options={languagesValues}
                    label={t('tags')}
                    name="tags"
                    onChange={onSelectTags}
                    isFilterable
                /> */}
                {dealerships.list.length !== 1 && <DealershipDropdown
                    defaultValue={
                        dealerships.list.length === 1
                            ? [
                                  {
                                      label: dealerships.list[0]['@id'].split('/')[4],
                                      value: dealerships.list[0]['@id'].split('/')[4]
                                  }
                              ]
                            : []
                    }
                    kvpsOnly
                    onChange={handleDealerSelection}
                />}
                <MultiSelectDropdown
                    options={languagesValues}
                    label={t('language') + " *"}
                    name="language-dropdown"
                    onChange={onSelectLanguage}
                    isSingleSelect
                    isFilterable
                />
                </>}
                <InputField
                    label={t('item_expiration_date')}
                    name="expiration_date"
                    onValueChangeHandler={(value) => setExpirationDate(value)}
                    value={expirationDate}
                    isActive
                    inputType="date"
                    minDate={today.toISOString().split('T')[0]}
                />
                <MultiSelectDropdown
                    options={AI_CLASSIFICATION}
                    defaultValue={AI_CLASSIFICATION.find(item => item.value === aiClassification) ? [AI_CLASSIFICATION.find(item => item.value === aiClassification)!] : []}
                    label={t('ai_classification') + " *"}
                    name="ai_classification_dropdown"
                    onChange={onSelectAIClassification}
                    isSingleSelect
                    isFilterable
                />                
                <MultiSelectDropdown
                    options={AI_DISCLOSURE}
                    defaultValue={AI_DISCLOSURE.find(item => item.value === aiDisclosureRequired) ? [AI_DISCLOSURE.find(item => item.value === aiDisclosureRequired)!] : []}
                    label={t('ai_disclosure') + " *"}
                    name="ai_disclosure_dropdown"
                    onChange={onSelectAIDisclosureRequired}
                    isSingleSelect
                    isFilterable
                />                
                <MultiSelectDropdown
                    options={AI_HUMAN_REVIEWED}
                    defaultValue={AI_HUMAN_REVIEWED.find(item => item.value === aiHumanReviewed) ? [AI_HUMAN_REVIEWED.find(item => item.value === aiHumanReviewed)!] : []}
                    label={t('ai_human_reviewed') + " *"}
                    name="ai_human_reviewed_dropdown"
                    onChange={onSelectAIHumanReviewed}
                    isSingleSelect
                    isFilterable
                />
               {assetBlob && <>
                    {assetBlob.type.split('/')[0] === 'image' && (
                        <img
                            src={URL.createObjectURL(assetBlob)}
                            alt="Uploaded"
                            className="uploadedImage"
                        />
                    )}
                    {assetBlob.type.split('/')[0] === 'video' && (
                        <video
                            src={URL.createObjectURL(assetBlob)}
                            className="uploadedVideo"
                        />
                    )}
                </>}
        </div>
    )
}

export default AssetMetadata;